import {
  DocumentationGroupV1,
  DocumentationItemConfigurationV1,
  DocumentationPageV1,
  SupernovaException,
} from "@supernova-studio/model";
import { DocumentationPageV1DTO } from "../../dto";
import {
  documentationItemConfigurationToDTOV1,
  getDtoDefaultItemConfigurationV1,
} from "./documentation-item-configuration-v1-to-dto";
import { buildDocPagePublishPaths, applyPrivacyConfigurationToNestedItems } from "./documentation-page-to-dto-utils";

// The fact that DTO conversion is located here instead of main backend code is due to the fact
// that we store page and group data in YJS documents in the same way as they are stored in the database.
// Therefore, we need to expose this conversion to the client so that it can consume data from YJS documents.
//
// Please do not put more DTO conversion here unless you know what you're doing.

export function documentationPagesToDTOV1(
  pages: DocumentationPageV1[],
  groups: DocumentationGroupV1[],
  routingVersion: string
): DocumentationPageV1DTO[] {
  const pathsMap = buildDocPagePublishPaths(groups, pages, routingVersion);

  return pages.map(page => documentationPageToDTOV1(page, pathsMap));
}

export function documentationPagesFixedConfigurationToDTOV1(
  pages: DocumentationPageV1[],
  groups: DocumentationGroupV1[],
  routingVersion: string
): DocumentationPageV1DTO[] {
  const pathsMap = buildDocPagePublishPaths(groups, pages, routingVersion);

  const { pages: fixedPages } = applyPrivacyConfigurationToNestedItems<
    DocumentationPageV1,
    DocumentationGroupV1,
    DocumentationItemConfigurationV1
  >(pages, groups, getDtoDefaultItemConfigurationV1);

  return fixedPages.map((page: DocumentationPageV1) => documentationPageToDTOV1(page, pathsMap));
}

function documentationPageToDTOV1(page: DocumentationPageV1, pagePathMap: Map<string, string>): DocumentationPageV1DTO {
  let path = pagePathMap.get(page.persistentId);
  if (!path) throw SupernovaException.conflict(`Path for page ${page.id} was not calculated`);

  if (path.startsWith("/")) path = path.substring(1);

  const configuration = documentationItemConfigurationToDTOV1(page.data.configuration);

  return {
    id: page.id,
    designSystemVersionId: page.designSystemVersionId,
    persistentId: page.persistentId,
    shortPersistentId: page.shortPersistentId,
    // backward compatibility with custom doc exporters, anyway these pages will not be shown, so we can adjust title
    title: configuration.isHidden && !page.meta.name.startsWith("_") ? "_" + page.meta.name : page.meta.name,
    blocks: page.data.blocks,
    slug: page.slug,
    userSlug: page.userSlug,
    configuration,
    createdAt: page.createdAt,
    updatedAt: page.updatedAt,
    path: path,
  };
}
